import { Component, Mixins, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import layout from '@/diagrams/configs/timeline-chart-layout'
import DiagramService from '@/app/services/diagram-data-service'
import saveAs from 'file-saver'
import { diagramViews, DiagramType, DiagramView } from '@/diagrams/models/diagram-views'
import map from 'lodash/map'
import minBy from 'lodash/minBy'
import max from 'lodash/max'
import maxBy from 'lodash/maxBy'
import orderBy from 'lodash/orderBy'
import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment'
import palette from 'google-palette'
import EditNodeEvent from '@/diagrams/events/edit-node-event'
import Node from '@/diagrams/models/node'
import Project from '@/projects/entities/project'
import projectsService from '@/projects/services/projects-service'
import TimelineDiagramConfiguration from './timeline-diagram-configuration'
import DiagramConfiguration from '@/projects/entities/diagram-configuration'
import CreateNodeEvent from '@/diagrams/events/create-node-event'
import Action from '@/diagrams/models/action'
import Visual from '@/diagrams/models/visual'

@Component
export default class Timeline extends Vue implements Visual {

  @Ref()
  private plotly!: any

  private fab = false

  private data = [
    {
      type: "scatter",
      mode: "lines+markers",
      visible: true,
      xaxis: "x",
      yaxis: "y",
      showlegend: true,
      xcalendar: "gregorian",
      ycalendar: "gregorian",
    },
  ]

  private layout = cloneDeep<any>(layout)

  private barShapeTemplate = {
    visible: true,
    layer: "above",
    name: "-",
    opacity: 1,
    fillcolor: "#f2d7d5",
    line: {
      width: 0,
      dash: "solid",
    },
    type: "rect",
    xsizemode: "scaled",
    ysizemode: "scaled",
    xref: "x",
    x0: "2009-02-01",
    x1: "2009-02-25",
    yref: "y",
    y0: 2.8,
    y1: 3.2,
  }

  private annotationTemplate = {
    visible: true,
    clicktoshow: false,
    opacity: 1,
    bgcolor: "rgba(0,0,0,0)",
    bordercolor: "rgba(0,0,0,0)",
    borderpad: 1,
    borderwidth: 1,
    showarrow: false,
    textangle: 0,
    font: {
      size: 13,
    },
    align: "left",
    captureevents: true,
    xref: "x",
    x: "2009-02-10",
    xanchor: "auto",
    xshift: 0,
    yref: "y",
    y: 3,
    yanchor: "middle",
    yshift: 0,
  }

  private milestoneAnnotationTemplate = {
    visible: true,
    showarrow: false,
    text: '',
    textangle: 0,
    font: {
      size: 14,
    },
    align: "left",
    captureevents: true,
    xref: "x",
    x: "2009-02-10",
    xanchor: "left",
    xshift: -5.7,
    yref: "y",
    y: 3,
    yanchor: "middle",
    yshift: 0,
  }

  private action: string = diagramViews.items
    .find((i) => i.type === DiagramType.Timeline)!
    .url

  private showDiagram = true
  private project: Project = new Project()
  private config: DiagramConfiguration = new DiagramConfiguration()

  @Ref()
  private diagramConfigurationDialog!: TimelineDiagramConfiguration

  public async setActionAsync(ctx: DiagramView) {
    this.action = ctx.url
    await this.fetchDataAsync()
    this.updateDiagram()
  }

  public async saveAsync(format: string) {
    if (format === 'svg') {
      const b64 = await this.plotly.toImage({ format: 'svg' })
      saveAs(b64, 'timeline.svg')
    } else if (format === 'png') {
      const b64 = await this.plotly.toImage()
      saveAs(b64, 'timeline.png')
    }
  }

  private clickAnnotation(data: any) {
    const actionId = data.annotation.id
    const node = new Node('node', 'Action', actionId)
    const evt = new EditNodeEvent(this.project, node)
    this.$bus.$emit('node:edit', evt)
  }

  private async addAction() {
    const evt = new CreateNodeEvent(this.project, new Node(), 'Action')
    this.$bus.$emit('node:create', evt)
  }

  private async fetchDataAsync() {
    const id = this.$route.params.projectId
    this.project = await projectsService.findAsync(id) || new Project()
    this.config = await projectsService.findDiagramConfiguration(id) || new DiagramConfiguration()
    const data = await DiagramService.timeline(id, this.action)
    const odata = orderBy(data, (a) => a.begin, 'asc')

    if (this.isFalse(this.config.timelineConfig.rangeMode)) {
      this.setDiagramRange(data)
    } else {
      this.setDiagramRange([
        {
          begin: this.config.timelineConfig.dateRange[0].date,
          end: this.config.timelineConfig.dateRange[1].date,
        },
      ])
    }

    this.layout.annotations = []
    this.layout.shapes = []
    const heights = this.assignLanes(odata)
    this.distributeActionBars(odata, heights)
    this.setDiagramHeight(odata, heights)
    this.addYaxisLabels(odata, heights)
  }

  private assignLanes(actions: any[]) {
    const numPhases = this.getMaximumPhaseNumber(actions)
    const heights: { [p: number]: number } = {}

    for (let phase = 1; phase <= numPhases; phase++) {
      const phaseActions = actions.filter((a) => a.phase === phase)
      const milestoneActions = phaseActions.filter((a) => a.isMilestone)
      const actionActions = phaseActions.filter((a) => a.isMilestone === false)

      let nrLanes = 1
      // Wir platzieren zuerst die Actions.
      for (const action of actionActions) {
        for (let lane = 1; lane <= nrLanes; lane++) {
          if (this.doesNotCollide(action, actionActions, lane)) {
            Vue.set(action, 'lane', lane)
            break
          }
        }
        // Could not assign action to existing lane. Add a new one.
        if (action.lane === undefined || action.lane === 0) {
          nrLanes++
          Vue.set(action, 'lane', nrLanes)
        }
      }

      // Milestones beginnen dann in der nächsten freien Lane. Wenn keine
      // Actions für diese Phase vorhanden sind ist das die 1.
      const newStart = actionActions.length > 0 ? nrLanes + 1 : 1

      // In einer neuen Lane platzieren wir jetzt die Milestones.
      for (const action of milestoneActions) {
        for (let lane = newStart; lane <= nrLanes; lane++) {
          if (this.doesNotCollide(action, milestoneActions, lane)) {
            Vue.set(action, 'lane', lane)
            break
          }
        }
        // Could not assign action to existing lane. Add a new one.
        if (action.lane === undefined || action.lane === 0) {
          nrLanes++
          Vue.set(action, 'lane', nrLanes)
        }
      }
      heights[phase] = nrLanes
    }
    return heights
  }

  private getMaximumPhaseNumber(actions: any[]) {
    return max(map(actions, 'phase')) || 1
  }

  private doesNotCollide(action: any, actions: any[], lane: number) {
    const collides = actions
      .filter((a) => a.lane === lane)
      .some((a) => {
        const begin = this.virtualBegin(action)
        const end = this.virtualEnd(a)
        return begin.isBefore(end)
      })
    return collides === false
  }

  private virtualBegin(action: any) {
    const begin = moment(action.begin)
    const len = this.nameLengthFactor(action)
    return moment.min(begin, begin.subtract(len, 'days'))
  }

  private virtualEnd(action: any) {
    const end = moment(action.end)
    const len = this.nameLengthFactor(action)
    return moment.max(end, end.add(len, 'days'))
  }

  private nameLengthFactor(action: any) {
    return Math.ceil(1.5 * (action.name.length / 2))
  }

  // TODO: Moment.js bietet isBefore, isAfter, isBetween, usw. an.
  // TODO: Vergleich mit < oder > ist u.U. problematisch.
  private insideOfRange(begin: any, end: any, rangeBegin: any, rangeEnd: any) {

    const beginn = moment(begin)
    const ende = moment(end)
    const rangeBeginn = moment(rangeBegin)
    const rangeEnde = moment(rangeEnd)

    if (beginn > rangeBeginn && ende < rangeEnde) {
      return true
    } else if (beginn < rangeBeginn && end > rangeBeginn) {
      return true
    } else if (beginn < rangeEnde && end > rangeEnde) {
      return true
    }
    return false
  }

  private distributeActionBars(actions: Action[], heights: { [p: number]: number }) {

    const colors = [
      'db9168',
      'd97b7a',
      'd3afa5',
      'f9c19e',
      'fedeab',
      'fff3a6',
      'acb9a4',
      '87dbce',
      // '5c9fba',
      '63aac6',
      // '5c76a0',
      '87abe5',
      'bdabc1',
      'f7bbb4',
      'd3e9be',
      'f1eac0',
      'b6a9a1',
      'cdd7d9']
      .concat(palette('cb-Spectral', Math.max(actions.length - 16, 1)))

    actions.forEach((action, i) => {
      const phaseOffset = this.getNumLanes(heights, action.phase - 1)
      const offset = phaseOffset + action.lane - 1

      // if (action.isMilestone) {
      //   this.addMilestone(action, offset)
      // } else {
      this.addActionBar(colors, offset, action, i)
      // }
    })
  }

  private getNumLanes(heights: { [p: number]: number }, upTo: number) {
    let sum = 0
    for (let i = 1; i <= upTo; i++) {
      sum += heights[i]
    }
    return sum
  }

  private addActionBar(colors: string[], offset: number, action: Action, i: number) {
    let begin = moment(action.begin)
    let end = moment(action.end)

    const rangeBegin = this.config.timelineConfig.dateRange && this.config.timelineConfig.dateRange.length === 2
      ? moment(this.config.timelineConfig.dateRange[0].date)
      : begin
    const rangeEnd = this.config.timelineConfig.dateRange && this.config.timelineConfig.dateRange.length === 2
      ? moment(this.config.timelineConfig.dateRange[1].date)
      : end

    if (this.isFalse(this.config.timelineConfig.rangeMode)
    || this.insideOfRange(begin, end, rangeBegin, rangeEnd)) {

      if (this.config.timelineConfig.rangeMode === "true") {
        if (begin < rangeBegin) {
          begin = rangeBegin
        }
        if (end > rangeEnd) {
          end = rangeEnd
        }
      }

      const duration = end.diff(begin, 'days')
      const bar = cloneDeep(this.barShapeTemplate)
      const height = 0.6

      // Add one day to the end date if the action is a single day event.
      if (begin.isSame(end, 'day')) {
        end = end.add(1, 'day')
      }

      bar.y0 = offset - height / 2
      bar.y1 = offset + height / 2
      bar.x0 = begin.format('YYYY-MM-DD')
      bar.x1 = end.format('YYYY-MM-DD')

      if (action.isMilestone) {
        bar.fillcolor = `#ffffff`
      } else {
        bar.fillcolor = `#${colors[i]}`
      }

      this.layout.shapes.push(bar)

      if (action.isMilestone) {
        const label = cloneDeep<any>(this.milestoneAnnotationTemplate)
        label.id = action.id
        label.y = offset
        label.x = moment(begin).format('YYYY-MM-DD')
        label.text = `♦ ${action.name}`
        this.addVerticalLineAt(label.x, offset)
        this.layout.annotations.push(label)
      } else {
        const label = cloneDeep<any>(this.annotationTemplate)
        label.id = action.id
        label.y = offset
        label.x = begin
          .add(Math.floor(duration / 2), 'days')
          .format('YYYY-MM-DD')
        label.text = action.name
        this.layout.annotations.push(label)
      }
    }
  }

  private setDiagramHeight(actions: Action[], heights: { [p: number]: number }) {
    const numPhases = this.getMaximumPhaseNumber(actions)
    const numLanes = this.getNumLanes(heights, numPhases)

    this.layout.height = 200 + numLanes * 40
  }

  private setDiagramRange(data: any[]) {
    let start = moment().format('YYYY-MM-DD')
    let end = moment().format('YYYY-MM-DD')

    if (this.isFalse(this.config.timelineConfig.rangeMode)) {

      const minRange = minBy(data, (d) => d.begin)
      const maxRange = maxBy(data, (d) => d.end)

      if (minRange) {
        start = minRange.begin
      }
      if (maxRange) {
        end = maxRange.end
      }
    } else {
      start = this.config.timelineConfig.dateRange[0].date
      end = this.config.timelineConfig.dateRange[1].date
    }

    this.layout.xaxis.range["0"] = moment(start).format('YYYY-MM-DD')
    this.layout.xaxis.range["1"] = moment(end).format('YYYY-MM-DD')
  }

  private addYaxisLabels(actions: Action[], heights: { [p: number]: number }) {
    const numPhases = this.getMaximumPhaseNumber(actions)
    const pns = this.config.timelineConfig.phaseNames
    let tickidx = 0

    this.addSeparatorLineAt(-0.5)

    this.layout.yaxis.ticktext = []
    this.layout.yaxis.tickvals = []
    let separatorY = 0
    for (let phase = 1; phase <= numPhases; phase++) {
      const pn = pns.find((p) => parseInt(p.number, 10) === phase)
      this.layout.yaxis.ticktext.push(pn ? pn.name : phase)
      this.layout.yaxis.tickvals.push(tickidx++)
      for (let lane = 1; lane < heights[phase]; lane++) {
        this.layout.yaxis.ticktext.push(' ')
        this.layout.yaxis.tickvals.push(tickidx++)
      }
      separatorY += heights[phase]
      this.addSeparatorLineAt(separatorY - 0.5)
    }
  }

  private addSeparatorLineAt(y: number) {
    this.layout.shapes.push({
      type: 'line',
      x0: this.layout.xaxis.range["0"],
      y0: y,
      x1: this.layout.xaxis.range["1"],
      y1: y,
      line: {
        color: '#999',
        width: .5,
        // dash: 'dot',
      },
    })
  }

  private addVerticalLineAt(x: string, y: number) {
    this.layout.shapes.push({
      type: 'line',
      x0: x,
      y0: -.5,
      x1: x,
      y1: y,
      line: {
        color: 'rgba(0, 0, 0, 0.5)',
        width: .5,
        dash: 'dot',
      },
    })
  }

  private async updateDiagramConfiguration(config: DiagramConfiguration) {
    const id = this.project.id
    await projectsService.updateDiagramConfigurationAsync(id, config)
    this.diagramConfigurationDialog.close()
    await this.fetchDataAsync()
    this.updateDiagram()
  }

  private async openDiagramConfigurationDialog() {
    const id = this.project.id
    const config = await projectsService.findDiagramConfiguration(id)
    if (config) {
      this.diagramConfigurationDialog.edit(config)
    }
  }

  private updateDiagram() {
    this.showDiagram = false
    Vue.nextTick(() => { this.showDiagram = true })
  }

  private async mounted() {
    await this.fetchDataAsync()
    this.updateDiagram()
    this.$bus.$on('reload-spider', async () => {
      await this.fetchDataAsync()
      this.updateDiagram()
    })
  }

  private isFalse(s: string): boolean {
    return !s || s === "false"
  }
}
